/**
 * Environment helpers shared by the api and worker config schemas (server spec §7.1).
 *
 * The Zod schemas themselves live next to their readers (`apps/api/src/config.ts`, `apps/worker/src/config.ts`);
 * this module holds what both need: the secret denylist used by the production refinement, the `TRUST_PROXY`
 * parser, the list of keys that must never be logged, and the browser-side `WebEnv`.
 */
import { z } from 'zod';

/** Browser-side env (Vite exposes only `VITE_*`). */
export const WebEnv = z.object({
  VITE_API_URL: z.url().default('http://localhost:4000'),
});
export type WebEnv = z.infer<typeof WebEnv>;

/** Keys whose values must never be logged or echoed. */
export const SECRET_ENV_KEYS = [
  'DATABASE_URL',
  'DATABASE_URL_MIGRATOR',
  'DATABASE_URL_WORKER',
  'REDIS_URL',
  'S3_SECRET_KEY',
  'AI_SIDECAR_TOKEN',
  'SMTP_URL',
  'SHARE_COOKIE_SECRET',
  'GUEST_JWT_SECRET',
  'PAT_PEPPER',
  'OIDC_CLIENT_SECRET',
  'POSTGRES_PASSWORD',
  'MINIO_ROOT_PASSWORD',
] as const;

export function isSecretEnvKey(key: string): boolean {
  return (SECRET_ENV_KEYS as readonly string[]).includes(key);
}

/** Copy of the env with secret values replaced by `[redacted]` (safe for startup logs). */
export function redactEnv<T extends Record<string, unknown>>(env: T): T {
  const out: Record<string, unknown> = { ...env };
  for (const k of Object.keys(out))
    if (isSecretEnvKey(k) && out[k] !== undefined && out[k] !== '') out[k] = '[redacted]';
  return out as T;
}

/**
 * Dependency-free SHA-256 (FIPS 180-4) over the UTF-8 bytes of `s`, hex encoded. The shared package is also
 * bundled into the browser, so `node:crypto` is not an option here and `crypto.subtle` is async (Zod refinements
 * are synchronous). Inputs are short secrets; speed is irrelevant.
 */
export function sha256Hex(s: string): string {
  const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ]);
  const bytes = new TextEncoder().encode(s);
  const bitLen = bytes.length * 8;
  const padded = new Uint8Array(Math.ceil((bytes.length + 9) / 64) * 64);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  const dv = new DataView(padded.buffer);
  dv.setUint32(padded.length - 8, Math.floor(bitLen / 0x100000000));
  dv.setUint32(padded.length - 4, bitLen >>> 0);
  const H = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const W = new Uint32Array(64);
  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));
  for (let off = 0; off < padded.length; off += 64) {
    for (let i = 0; i < 16; i++) W[i] = dv.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const w15 = W[i - 15]!;
      const w2 = W[i - 2]!;
      const s0 = rotr(w15, 7) ^ rotr(w15, 18) ^ (w15 >>> 3);
      const s1 = rotr(w2, 17) ^ rotr(w2, 19) ^ (w2 >>> 10);
      W[i] = (W[i - 16]! + s0 + W[i - 7]! + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, h] = H as unknown as [
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
    ];
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[i]! + W[i]!) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    H[0] = (H[0]! + a) >>> 0;
    H[1] = (H[1]! + b) >>> 0;
    H[2] = (H[2]! + c) >>> 0;
    H[3] = (H[3]! + d) >>> 0;
    H[4] = (H[4]! + e) >>> 0;
    H[5] = (H[5]! + f) >>> 0;
    H[6] = (H[6]! + g) >>> 0;
    H[7] = (H[7]! + h) >>> 0;
  }
  return Array.from(H, (x) => x.toString(16).padStart(8, '0')).join('');
}

/**
 * SHA-256 of every secret that ships in `src/.env.example` (and of the compose dev credentials). A production
 * process must never run with one of these: they are public on GitHub (ops review 2026-10-21, H6). Hashes rather
 * than the values so the denylist itself is not another copy of the secret.
 */
export const EXAMPLE_SECRET_SHA256: ReadonlySet<string> = new Set([
  'b9a627f3e0b48fc1ae71731ce60e8271545e45363c911659cde8b766537c36ec', // AI_SIDECAR_TOKEN
  '7531037b14ed152b6090723f3d3c19087dca4fb5589191c8df253a636d134eb9', // SHARE_COOKIE_SECRET
  '46ed8657b64c3839e9986a32f403eba80c989c2e32275a04689e891164b3cb42', // GUEST_JWT_SECRET
  'e8b92256ff13e1504a8a6a7777f794170ce01da739182ab9d300d4daad41cddd', // PAT_PEPPER
  '456f45fddc2e725c2948d03317e2262c2ec8e86745af28dc7b2221987b858b50', // lumen
  '0ceb554a4b00a2bfe037bedb9e29ae16e1a785affed27d81291142579eef9b1e', // lumenlumen
]);

/** Values people type when they mean "I'll fix this later". Matched case-insensitively as the whole value. */
const WEAK_SECRET_WORDS = new Set([
  'changeme',
  'change-me',
  'secret',
  'password',
  'test',
  'dev',
  'development',
  'example',
  'placeholder',
  'todo',
  'xxx',
  'lumen',
  'lumenlumen',
]);

export function isExampleSecret(value: string): boolean {
  return EXAMPLE_SECRET_SHA256.has(sha256Hex(value));
}

/**
 * Why a value is not acceptable as a production secret, or `null` when it is. Checks, in order: the
 * `.env.example` denylist, a minimum length, obvious filler words, and a crude entropy floor (at least eight
 * distinct characters and no single character making up more than half of the value). `openssl rand -hex 32`
 * and longer always pass.
 */
export function weakSecretReason(value: string, opts: { minLength?: number } = {}): string | null {
  const minLength = opts.minLength ?? 32;
  if (isExampleSecret(value))
    return 'is the placeholder from .env.example (regenerate: openssl rand -hex 64)';
  if (value.length < minLength) return `must be at least ${minLength} characters`;
  if (WEAK_SECRET_WORDS.has(value.toLowerCase())) return 'is a well-known placeholder word';
  const counts = new Map<string, number>();
  for (const ch of value) counts.set(ch, (counts.get(ch) ?? 0) + 1);
  if (counts.size < 8) return 'has too little variety (fewer than 8 distinct characters)';
  const max = Math.max(...counts.values());
  if (max > value.length / 2) return 'is mostly one repeated character';
  return null;
}

/** Fastify `trustProxy` value: `false`, a hop count, or a list of trusted addresses / CIDRs. */
export type TrustProxy = boolean | number | string[];

/**
 * `TRUST_PROXY` env → Fastify `trustProxy`.
 *   `false` / empty  → nothing is trusted; `req.ip` is the socket peer.
 *   `true`           → exactly one trusted hop (the shipped nginx). Never "trust every hop": with that, any client
 *                      can pick its own `X-Forwarded-For` and therefore its own rate-limit key (H1).
 *   `2`, `3`, …      → that many trusted hops counted from the right of `X-Forwarded-For`.
 *   `10.0.0.0/8,172.16.0.0/12` → trust exactly these peers (Fastify / proxy-addr semantics), any depth.
 */
export function parseTrustProxy(raw: string | boolean | number | undefined): TrustProxy {
  if (raw === undefined || raw === null) return false;
  if (typeof raw === 'boolean') return raw ? 1 : false;
  if (typeof raw === 'number') return raw > 0 ? Math.floor(raw) : false;
  const s = raw.trim();
  if (s === '' || /^(0|false|no|off)$/i.test(s)) return false;
  if (/^(true|yes|on)$/i.test(s)) return 1;
  if (/^\d+$/.test(s)) return Number(s) > 0 ? Number(s) : false;
  const list = s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  if (!list.length) return false;
  for (const entry of list) {
    if (!/^[0-9a-fA-F.:]+(\/\d{1,3})?$/.test(entry))
      throw new Error(`TRUST_PROXY: "${entry}" is not a hop count, boolean, IP or CIDR`);
  }
  return list;
}
